/**
 * Catering domain models — vendors, menus, orders, cart, capabilities.
 *
 * All money values are in integer pence (e.g., 385 = £3.85) to avoid
 * floating-point errors. Use formatCurrencyMinor() from core/utils/catering.ts
 * to display as currency.
 */
import { DeliveryTargetType } from '../constants/enums';

// ── Vendor ─────────────────────────────────────────────

export interface CateringVendor {
  /**
   * ISO 4217 code this vendor's new menus are created in. A PREFILL, not a
   * rule — an individual menu may be priced in something else, and the order
   * freezes the published MENU's currency. Absent on documents written before
   * the field existed — and GraphQL sends those as `null`, not `undefined`,
   * so always guard with `|| 'GBP'` (a default parameter does not catch null).
   */
  defaultCurrency?: string | null;
  _id: string;
  name: string;
  vendorType: string;
  locationIds: string[];
  coverageFloorIds: string[];
  contactEmail: string;
  contactPhone?: string;
  description?: string;
  logo?: string;
  sourceLanguage: string;
  tags: string[];
  isFeatured?: boolean;
  enabled: boolean;
  cutOffMinutes?: number;
  cancellationCutOffMinutes?: number;
  bookingCancellationAction?: 'cancel' | 'review';
  estimatedDeliveryMinutes?: number;
  operatingHours?: VendorOperatingHours[];
  operatingHoursExceptions?: VendorHoursException[];
  notificationEmails?: string[];
  ratings?: { average: number; count: number };
  orderCount?: number;
  approvalThresholdPence?: number;
  approvalEnabled?: boolean;
  acceptsSameDay?: boolean;
  onlineOrdering?: boolean;
  /**
   * When true the vendor accepts orders that are not attached to a meeting
   * booking. Drives the homepage "Order Catering" flow that delivers to a
   * floor or ad-hoc target. Backend default falls back from vendorType:
   * internal_catering + onsite_vendor → true; external_provider + email_only → false.
   */
  allowsAdHocOrders?: boolean;
  coverImage?: string;
  menuCount?: number;
  /** Enabled menus with at least one publication — what an employee can
   *  order from. `menuCount` includes drafts (qlcw.4). */
  publishedMenuCount?: number;
  menuItemCount?: number;
}

export interface VendorOperatingHours {
  dayOfWeek: number;
  open: string;
  close: string;
}

export interface VendorHoursException {
  date: string;
  closed: boolean;
  open?: string;
  close?: string;
  reason?: string;
}

// ── Menu ───────────────────────────────────────────────

export interface CateringMenu {
  _id: string;
  vendorId: string;
  name: string;
  description?: string;
  enabled: boolean;
  sourceLanguage: string;
  currency?: string;
  categories: string[];
  /** Optional second-level grouping names referenced by item.subCategory. */
  subCategories?: string[];
  items: CateringMenuItem[];
  publishedVersion?: number;
  hasUnpublishedChanges?: boolean;
}

export interface CateringMenuItem {
  _id: string;
  name: string;
  description?: string;
  category?: string;
  /** Optional second-level grouping. Must be in the parent menu's subCategories[]. */
  subCategory?: string;
  /** External URL for off-site menu items (e.g. Pret, Just Eat). */
  sourceUrl?: string;
  /** Lead-in minutes the vendor needs before the meeting starts. */
  setupMinutes?: number;
  costPence?: number;
  currency?: string;
  prices?: Record<string, number>;
  unit?: string;
  image?: string;
  allergens?: MenuItemAllergen[];
  dietaryTags: string[];
  cutOffMinutes?: number;
  minQuantity?: number;
  maxQuantity?: number;
  enabled: boolean;
  availableFrom?: string;
  availableUntil?: string;
  /** 0 = MONDAY. The platform's indexing, not JS `getDay`. */
  availableDays?: number[];
  /** The tax rate an order line for this item will carry, in percent — the
   *  vendor's own rate, else the region's default. Prices are EX tax; the
   *  order's total is not (qlcw.8). */
  taxRatePercent?: number;
  questions?: MenuItemQuestion[];
  isFeatured?: boolean;
  displayOrder?: number;
  prepNotes?: string;
  taxCategory?: string;
  sku?: string;
  ingredients?: string[];
  translatedName?: string;
  translatedDescription?: string;
  translatedQuestions?: { name: string; options: string[] }[];
}

export interface MenuItemAllergen {
  id: string;
  label: string;
}

export interface MenuItemQuestion {
  name: string;
  fieldType: string;
  required: boolean;
  options?: { label: string; pricePence: number }[];
}

// ── Cart (ephemeral, not from API) ─────────────────────

/**
 * Each CartItem is a unique configuration of a menu item.
 * Same menuItemId with different options = separate line items.
 * E.g., "Latte (Oat, Primo)" and "Latte (Almond, Massimo)" are two CartItems.
 * "10x Latte (Oat, Primo)" is one CartItem with quantity 10.
 *
 * `cartLineId` uniquely identifies this line — derived from menuItemId + sorted options hash.
 */
export interface CartItem {
  /** Unique ID for this cart line — menuItemId + options hash */
  cartLineId: string;
  menuItemId: string;
  menuId: string;
  name: string;
  quantity: number;
  unitCostPence: number;
  totalCostPence: number;
  dietaryTags: string[];
  allergens: MenuItemAllergen[];
  category?: string;
  specialInstructions?: string;
  questionAnswers?: { questionName: string; answer: string }[];
  /**
   * The rate this line will be taxed at, in percent, from the published menu
   * item. Prices are EX tax; the order's total is not. Absent on a line saved
   * before the field existed, which then counts as untaxed in the estimate.
   */
  taxRatePercent?: number;
}

/** Generate a unique cart line ID from menu item ID and option answers */
export function cartLineId(
  menuItemId: string,
  answers?: { questionName: string; answer: string }[],
): string {
  if (!answers?.length) return menuItemId;
  const sorted = [...answers].sort((a, b) => a.questionName.localeCompare(b.questionName));
  const hash = sorted.map(a => `${a.questionName}=${a.answer}`).join('|');
  return `${menuItemId}::${hash}`;
}

// ── Order ──────────────────────────────────────────────

export interface CateringOrder {
  _id: string;
  orderNumber: number;
  vendorId: string;
  vendorName: string;
  vendorType: string;
  status: CateringOrderStatus;
  metadata?: { bookingCancellationNeedsReview?: boolean; bookingCancellationReviewReason?: string };
  bookingId?: string;
  locationId: string;
  locationName?: string;
  divisionId?: string;
  divisionName?: string;
  bookingType?: string;
  meetingTitle?: string;
  orderedBy: OrderUser;
  orderedFor?: OrderUser;
  deliveryTarget: DeliveryTarget;
  deliveryTime: string;
  timezoneId?: string;
  items: OrderItemSnapshot[];
  freeTextRequest?: string;
  costCentre?: string;
  clientMatterNumber?: string;
  glCode?: string;
  billingSplit?: BillingSplit[];
  specialInstructions?: string;
  // Money breakdown (all in integer minor units)
  subtotalPence?: number;
  taxPence?: number;
  totalPence?: number;
  /** Legacy alias kept for back-compat with CateringApiService callers. */
  totalCostPence?: number;
  discountPence?: number;
  serviceChargePence?: number;
  deliveryFeePence?: number;
  currency?: string;
  approval?: OrderApproval;
  statusHistory: StatusHistoryEntry[];
  modifications: OrderModification[];
  vendorNotifiedAt?: string;
  cancelledReason?: string;
  // Status timestamps
  submittedAt?: string;
  acceptedAt?: string;
  rejectedAt?: string;
  cancelledAt?: string;
  inPreparationAt?: string;
  inDeliveryAt?: string;
  deliveredAt?: string;
  completedAt?: string;
  // SLA + facets
  responseTimeMinutes?: number;
  leadTimeMinutes?: number;
  deliveryDateLocal?: string;
  deliveryHourOfDay?: number;
  deliveryDayOfWeek?: number;
  deliveryMonth?: string;
  deliveryQuarter?: string;
  deliveryWeekKey?: string;
  // Counts + drift
  itemCount?: number;
  uniqueItemCount?: number;
  headcountTotal?: number;
  republishedSinceSubmit?: boolean;
  priceDriftPence?: number;
  menuPublicationId?: string;
  menuVersion?: number;
  // Concurrency
  version?: number;
  lastEditedAt?: string;
}

export interface OrderUser {
  _id: string;
  name?: string;
  email?: string;
}

/**
 * DeliveryTarget — wire-format shape for `CreateCateringOrderInput.deliveryTarget`.
 *
 * `type` MUST be one of the canonical GraphQL enum keys (see
 * `core/constants/enums.ts:DeliveryTargetType`). Lowercase strings
 * coerce to `BAD_USER_INPUT` server-side.
 */
export interface DeliveryTarget {
  type: DeliveryTargetType;
  spaceId?: string;
  /** Optional space type hint when `type === 'SPACE'`. Examples:
   *  `'meetingroom' | 'desk' | 'zone' | 'parking' | 'focusbooth'`. */
  spaceType?: string;
  floorId?: string;
  locationId: string;
  name?: string;
  instructions?: string;
}

export interface OrderItemSnapshot {
  _id: string;
  menuItemId: string;
  menuId: string;
  menuPublicationId?: string;
  menuVersion?: number;
  name: string;
  description?: string;
  descriptionHtml?: string;
  specialInstructions?: string;
  quantity: number;
  unitCostPence?: number;
  totalCostPence?: number;
  subtotalPence?: number;
  totalPence?: number;
  taxPence?: number;
  taxCategory?: string;
  taxRatePercent?: number;
  currency?: string;
  unit?: string;
  dietaryTags?: string[];
  allergens?: MenuItemAllergen[];
  category?: string;
  subCategory?: string;
  sourceLanguage?: string;
  sourceUrl?: string;
  setupMinutes?: number;
  imageUrl?: string;
  ingredients?: string[];
  sku?: string;
  priceDriftPence?: number;
  questionAnswers?: OrderItemQuestionAnswer[];
}

export interface OrderItemQuestionAnswer {
  questionId?: string;
  questionName: string;
  answer: string;
  surchargePence?: number;
}

export interface BillingSplit {
  costCentre: string;
  percentage: number;
  amountPence?: number;
}

export interface OrderApproval {
  required: boolean;
  threshold?: number;
  approvedBy?: { _id: string; name?: string; email?: string; timestamp?: string };
  rejectedBy?: { _id: string; name?: string; email?: string; timestamp?: string };
  rejectedReason?: string;
}

export interface StatusHistoryEntry {
  status: string;
  previousStatus?: string;
  changedBy: OrderUser;
  changedAt: string;
  reason?: string;
  /** Minutes spent in the previous status before transitioning. */
  transitionDuration?: number;
}

export interface OrderModification {
  field: string;
  oldValue?: unknown;
  newValue?: unknown;
  changedBy: OrderUser;
  changedAt: string;
}

// ── Capabilities (from cateringCapabilities query) ─────

export interface CateringCapabilities {
  enabled: boolean;
  vendors: CateringVendorSummary[];
  vendorCount: number;
  canOrderForOthers: boolean;
  approvalThresholdPence?: number;
  approvalEnabled: boolean;
  locationTimezone: string;
  currency: string;
  costCentreMandatory: boolean;
  costCentreValues: string[];
  aiAssistEnabled: boolean;
}

export interface CateringVendorSummary {
  _id: string;
  name: string;
  logo?: string;
  tags: string[];
  vendorType: string;
  isOpen: boolean;
  estimatedDeliveryMinutes?: number;
}

// ── Validation (from pre-submit queries) ───────────────

export interface VendorAvailability {
  available: boolean;
  reason?: string;
  nextAvailableSlot?: string;
  operatingHours?: { openTime: string; closeTime: string };
  exceptionNote?: string;
}

export interface CutOffCheck {
  canOrder: boolean;
  cutOffTime?: string;
  minutesUntilCutOff?: number;
  blockedItems?: string[];
  reason?: string;
}

export interface ApprovalCheck {
  required: boolean;
  thresholdPence?: number;
  enabled: boolean;
}

export interface CartValidation {
  valid: boolean;
  issues: CartItemIssue[];
}

export interface CartItemIssue {
  menuItemId: string;
  itemName: string;
  issueType: 'UNAVAILABLE' | 'PRICE_CHANGED' | 'MAX_QTY_EXCEEDED' | 'MIN_QTY_NOT_MET' | 'OUTSIDE_AVAILABILITY';
  message: string;
  currentPricePence?: number;
  maxQuantity?: number;
  minQuantity?: number;
}

// ── Order Draft (ephemeral, for the booking panel) ─────

export interface CateringOrderDraft {
  vendorId: string;
  vendorName: string;
  vendorLogo?: string;
  vendorType: string;
  items: CartItem[];
  freeTextRequest?: string;
  headcount?: number;
  totalCostPence: number;
  currency: string;
  deliveryTime: Date;
  deliveryTarget: DeliveryTarget;
  specialInstructions?: string;
  dietaryConfirmed: boolean;
  idempotencyKey: string;
  /** Which room this catering is for (in multi-room meetings) */
  roomSpaceId: string;
  roomName: string;
  /** Which day in a multi-day meeting (0-based). Undefined for single-day. */
  dayIndex?: number;
}

// ── API Input Types ───────────────────────────────────

/** Input for a single item when validating or creating an order. */
export interface CateringOrderItemInput {
  menuItemId: string;
  menuId: string;
  name?: string;
  quantity: number;
  unitCostPence?: number;
  /** Per-item note for the vendor (e.g. selected modifier options). The
   *  backend's CateringOrderItemInput accepts it; older callers omit it. */
  specialInstructions?: string;
}

/** Input for the createCateringOrder mutation. */
export interface CreateCateringOrderInput {
  vendorId: string;
  locationId: string;
  /** The meeting's room booking. Absent for an order to a floor or a drop-off point: the API refuses ''. */
  bookingId?: string;
  /**
   * A meeting made in Outlook, with no booking (`rdx_platform_api-tshy.1`):
   * the schedule row's id (the room copy). Instead of `bookingId`, never with
   * it. The API delivers to the meeting's room and decides who may order.
   */
  calendarEventId?: string;
  deliveryTarget: DeliveryTarget;
  deliveryTime: string;
  items: CateringOrderItemInput[];
  freeTextRequest?: string;
  costCentre?: string;
  specialInstructions?: string;
  idempotencyKey?: string;
}

// ── Menu Publication ───────────────────────────────────

export interface PublicationFieldChange {
  field: string;
  label: string;
  oldValue?: string;
  newValue?: string;
}

export interface PublicationChange {
  type: 'added' | 'removed' | 'modified';
  itemId: string;
  itemName: string;
  fields?: PublicationFieldChange[];
  category?: string;
}

export interface CateringMenuPublication {
  _id: string;
  menuId: string;
  version: number;
  publishedAt: string;
  publishedBy: string;
  changelog?: string;
  itemCount?: number;
  changes?: PublicationChange[];
  changeSummary?: { added: number; removed: number; modified: number };
}

// ── Paged orders + admin filters ──────────────────────

export interface PagedCateringOrders {
  orders: CateringOrder[];
  count: number;
  nextCursor?: string | null;
}

export interface CateringOrdersAdminFilters {
  locationId?: string;
  status?: CateringOrderStatus;
  vendorId?: string;
  from?: string;
  to?: string;
  searchTerm?: string;
  divisionId?: string;
  requesterUserId?: string;
  costCentre?: string;
  bookingType?: string;
  republishedOnly?: boolean;
  hasUnresolvedApproval?: boolean;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  cursor?: string;
  skip?: number;
  take?: number;
}

export interface VendorOrdersFilters {
  status?: CateringOrderStatus;
  from?: string;
  to?: string;
  searchTerm?: string;
  cursor?: string;
  skip?: number;
  take?: number;
}

// ── Type Alias ─────────────────────────────────────────

export type CateringOrderStatus =
  | 'draft'
  | 'pending_approval'
  | 'confirmed'
  | 'in_preparation'
  | 'in_delivery'
  | 'delivered'
  | 'completed'
  | 'cancelled'
  | 'rejected';

// ── Vendor Users (Team) ─────────────────────────────────

export type VendorUserRole = 'owner' | 'editor';
export type VendorUserStatus = 'invited' | 'active' | 'disabled';

export interface CateringVendorUser {
  _id: string;
  vendorId: string;
  email: string;
  displayName?: string;
  role: VendorUserRole;
  status: VendorUserStatus;
  invitedAt?: string;
  invitedBy?: string;
  lastSignInAt?: string;
}
